import React, { useState } from 'react';

import PromoCode from '@editor/components/PromoCode/PromoCode';
import analytics from '@repo/analytics';
import { withEventScope } from '@repo/event-processor';
import {
  subscriptionApi,
  SubscriptionPlanType,
  type Subscription,
} from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import {
  Button,
  primaryTheme,
  secondaryTheme,
} from '@repo/ui/components/Buttons';
import { fireToast } from '@repo/ui/components/Toast';

import { SUBSCRIPTION_UPGRADE_SUCCESS } from '../../../../global/analytics/events';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../../../stores/planSelectionStore';
import { useUserStore, userStoreSelector } from '../../../../stores/userStore';
import DecisionModal from '../../../DecisionModal/DecisionModal';
import useBillingPrices from '../../hooks/useBillingPrices';
import useSubscriptionChangeState from '../../hooks/useSubscriptionChangeState';
import OffboardingModal from '../../OffboardingModal/OffboardingModal';
import usePromoCodeManagement from '../../SubscriptionModal/hooks/usePromoCodeManagement';
import { PromoCodeWrapper } from '../../SubscriptionModal/VariantFlows/styles';

import { Buttons } from './styles';
import { ChangeSubscriptionActionsProps } from './types';
import { getChangeLabel, isScheduledChange } from './utils';

enum Modals {
  SchedueChange = 'SCHEDULE_CHANGE',
  Offboarding = 'OFFBOARDING',
}

/**
 * This component handles the actions related to changing a subscription, including upgrading, downgrading, renewing, and canceling subscriptions.
 * It also manages the display of promo codes and handles the submission of subscription changes.
 */
const ChangeSubscriptionActions: React.FC<ChangeSubscriptionActionsProps> = ({
  disabled,
  currentSubscription,
  onSuccess,
  subscriptionChangePreviewData,
  showStackedLayout,
  isUpgrade,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [shownModal, setShownModal] = useState<Modals | null>(null);

  const user = useUserStore(userStoreSelector.user);
  const updateUserInfo = useUserStore(userStoreSelector.updateUserInfo);

  const { onPromoCodeApplication, onPromoCodeRemoval } =
    usePromoCodeManagement();

  const { setUpgrade, setSelectionDisabled } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );

  const promoCode = usePlanSelectionStore(planSelectionStoreSelector.promoCode);

  const { setPromoCode } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );

  const updateSubscription = useSubscriptionStore(
    subscriptionStoreSelector.updateSubscription
  );

  const openBillingPortal = useSubscriptionStore(
    subscriptionStoreSelector.openBillingPortal
  );

  // Get the billing prices and subscription change state
  const pricesToChange = useBillingPrices();
  const isCanceled = !!currentSubscription?.canceledAt;
  const isSubscriptionChanged = !!useSubscriptionChangeState({
    currentSubscription,
  });

  // Handle subscription change success
  const handleSubscriptionChanged = (subscription: Subscription): void => {
    updateSubscription(subscription);
    onSuccess?.();
  };

  if (!user) {
    return null;
  }

  const {
    preview,
    isLoading: changePreviewLoading,
    missingPaymentMethod,
  } = subscriptionChangePreviewData;

  /**
   * This function handles the subscription change action.
   */
  const changeSubscription = async (): Promise<void> => {
    if (!pricesToChange) {
      return;
    }
    setIsLoading(true);
    setSelectionDisabled(true);

    const response = await subscriptionApi.changeSubscription({
      localPrices: pricesToChange,
      promoCode: promoCode?.code,
    });

    fireToast({
      label:
        'success' in response
          ? 'Your subscription was changed'
          : `Failed to change your subscription. Please contact our support. ${response.error}`,
      error: !('success' in response),
      duration: 5000,
    });
    if ('success' in response && response.success) {
      const subscription = currentSubscription;
      handleSubscriptionChanged(response.subscription);

      // If change is successful, set promo code to null to avoid rendering wrong information.
      setPromoCode(null);

      void updateUserInfo();
      const upgradeSucceeded =
        user &&
        subscription &&
        subscription.plan?.name === SubscriptionPlanType.PRO &&
        response.subscription.plan?.name === SubscriptionPlanType.EXPERT;

      // track upgrade success
      if (upgradeSucceeded) {
        await analytics.track(SUBSCRIPTION_UPGRADE_SUCCESS, {
          userId: user.id,
          leadsUserData: {
            email: user.email,
          },
          plan: response.subscription.plan!.name, // is ensured by upgradeSucceeded
        });
      }

      setUpgrade(false);
    }
    setIsLoading(false);
    setSelectionDisabled(false);
  };

  // Define the buttons to be displayed based on the subscription state: cancelled, changed, or new subscription
  const buttons: Array<React.ReactElement> = [];
  if (!isSubscriptionChanged) {
    buttons.push(
      <Button
        disabled={disabled}
        height="40px"
        href="#"
        label="View Billing Information"
        name="viewBillingInfoButton"
        theme={secondaryTheme}
        width="100%"
        onClick={openBillingPortal}
      />
    );
    // If the subscription is canceled, show the renew subscription button
    if (isCanceled) {
      const handleRenewSubscription = async (): Promise<void> => {
        setIsLoading(true);
        setSelectionDisabled(true);
        const response = await subscriptionApi.renewSubscription();
        fireToast({
          label:
            'success' in response
              ? 'Your subscription was renewed'
              : `Failed to renew your subscription. Please contact our support. ${response.error}`,
          error: !('success' in response),
          duration: 5000,
        });
        if ('success' in response && response.success) {
          handleSubscriptionChanged(
            response.subscription as unknown as Subscription
          );
        }
        setIsLoading(false);
        setSelectionDisabled(false);
      };

      buttons.push(
        <Button
          disabled={disabled}
          height="40px"
          label="Renew Subscription"
          loading={isLoading}
          name="renewSubscriptionButton"
          theme={primaryTheme}
          width="100%"
          onClick={handleRenewSubscription}
        />
      );
    } else {
      buttons.push(
        <Button
          disabled={disabled}
          height="40px"
          label="Cancel Subscription"
          name="cancelSubscriptionButton"
          theme={secondaryTheme}
          width="100%"
          onClick={(): void => setShownModal(Modals.Offboarding)}
        />
      );
    }
  } else if (currentSubscription?.plan) {
    if (missingPaymentMethod) {
      // If user is missing payment method, show update payment info button
      buttons.push(
        <Button
          disabled={disabled}
          height="40px"
          label="Update Payment Info"
          name="updatePaymentInfoButton"
          theme={primaryTheme}
          width="100%"
          onClick={openBillingPortal}
        />
      );
    }

    // If user is upgrading a subscription, display promo code input. Downgrade or editing an existing subscription is not supported.
    if (isUpgrade && isSubscriptionChanged) {
      buttons.push(
        <PromoCodeWrapper>
          <PromoCode
            disabled={false}
            onApplyClick={onPromoCodeApplication}
            onRemoveClick={onPromoCodeRemoval}
          />
        </PromoCodeWrapper>
      );
    }
    // Show the confirm change button
    buttons.push(
      <Button
        disabled={disabled || missingPaymentMethod}
        height="40px"
        label="Confirm Change"
        loading={changePreviewLoading || isLoading}
        name="confirmChangeButton"
        width="100%"
        onClick={async (): Promise<void> => {
          if (isScheduledChange(preview)) {
            setShownModal(Modals.SchedueChange);
            return;
          }
          await changeSubscription();
        }}
      />
    );
  }

  return (
    <>
      <Buttons
        numberOfButtons={buttons.length}
        showStackedLayout={showStackedLayout}
      >
        {buttons.map((button, index) => (
          <React.Fragment key={index}>{button}</React.Fragment>
        ))}
      </Buttons>
      <OffboardingModal
        isOpen={shownModal === Modals.Offboarding}
        subscription={currentSubscription}
        user={user}
        onClose={(): void => setShownModal(null)}
        onSuccess={handleSubscriptionChanged}
      />
      <DecisionModal
        useNewDialog
        firstButton={{
          label: 'Cancel',
          width: '148px',
          onClick: (): void => setShownModal(null),
        }}
        isOpen={shownModal === Modals.SchedueChange}
        message={
          <>
            Your subscription change will be scheduled at the end of your
            current billing cycle.
            <br />
            <br />
            Change plan:
            <br />
            <b>{getChangeLabel(preview)}</b>
          </>
        }
        secondButton={{
          label: 'Confirm',
          width: '148px',
          onClick: changeSubscription,
        }}
        title="Schedule change"
        onClose={(): void => setShownModal(null)}
      />
    </>
  );
};

export default withEventScope(ChangeSubscriptionActions, {
  module: 'changeSubscriptionActions',
});
